<template>
  <div class="flex flex-col items-center lg:items-start bg-white
    rounded-4xl sm:rounded-20px py-20 px-40 lg:px-20 lg:py-10 sm:px-5 md:px-10 sm:py-20">
      <h1 v-if="currentStep !== 4" class="w-full text-44px font-bold text-neutral-50 sm:hidden">Анкета кандидата</h1>
      <div v-if="currentStep !== 4" class="flex w-2/3 xl:w-full pt-12 sm:pt-0">
        <ProgressBar :current-step="currentStep" maxSteps="2" />
      </div>
    <div
      v-if="currentStep !== 4"
      class="flex justify-center w-2/3 xl:w-full mt-8 mb-10 font-bold text-32px sm:text-2xl"
    >
      {{currentTitle}}
    </div>
    <keep-alive>
      <component
        :is="currentInterface"
        :nationality-options="nationalityOptions"
        :regions-options="regionsData"
        :offices-options="officesData"
        :schedules-options="schedulesOptions"
        :vacancies-options="vacanciesOptions"
        :telegram-link="telegramLink"
        @user-info-ready="userInfoReadyHandler"
        @vacancy-info-ready="vacancyInfoReadyHandler"
        @question-info-ready="questionInfoReadyHandler"
        @go-prev="goPrevStep"></component>
    </keep-alive>
    <DialogInfo />
  </div>
</template>

<script>
import ProgressBar from '@/components/Bars/ProgressBar.vue';
import UserInfo from '@/components/Franchise/UserInfo.vue';
import VacancyInfo from '@/components/Franchise/VacancyInfo.vue';
import LastScreen from '@/components/Interfaces/LastScreen.vue';
import DialogInfo from '@/components/UI/DialogInfo.vue';
import sanitize from '@/sanitize';

import {
  http,
  getCountries,
  getCandidateRegions,
  getOffices,
  getWorkSchedules,
  getVacancies,
  postCandidate,
  postResume,
  checkDuplicates,
  updateCandidateDetail,
  updateCandidateData,
  aplicationConfirm,
  getTelegramLink,
} from '@/api';
import toast from '@/notify';

export default {
  name: 'MainContent',
  components: {
    ProgressBar,
    UserInfo,
    VacancyInfo,
    DialogInfo,
    LastScreen,
  },
  data() {
    return {
      currentStep: 1,
      minStep: 1,
      maxStep: 4,
      titles: new Map([
        [1, 'Заполни личные данные'],
        [2, 'Выбери вакансию'],
        [3, 'Ответь на вопросы'],
      ]),
      interfaces: new Map([
        [1, 'UserInfo'],
        [2, 'VacancyInfo'],
        [3, 'QuestionsInfo'],
        [4, 'LastScreen'],
      ]),
      nationalityData: [],
      regionsData: [],
      officesData: [],
      schedulesData: [],
      vacanciesData: [],
      telegramLink: null,
      preferredCommunicationMap: new Map([
        ['Телефон', 'PHONE'],
        ['E-mail', 'EMAIL'],
        ['Telegram', 'TELEGRAM'],
        ['WhatsApp', 'WA'],
        ['Viber', 'VIBER'],
        ['VK', 'VK'],
      ]),
    };
  },
  mounted() {
    const queue1 = [
      getCountries(),
      getWorkSchedules(),
    ];

    const queue2 = [
      getCandidateRegions('FRANCHISE'),
      getOffices('FRANCHISE'),
      getVacancies('FRANCHISE'),
    ];

    const setDataMap = new Map([
      ['/countries', 'nationalityData'],
      ['/regions-candidate', 'regionsData'],
      ['/offices-candidate', 'officesData'],
      ['/vacancies-candidate/positions', 'vacanciesData'],
      ['/work-schedules', 'schedulesData'],
    ]);

    Promise.all(queue1).then((response) => {
      Array.from(response).forEach(({ config, data }) => {
        this[setDataMap.get(config.url)] = data;
      });
    });

    Promise.all(queue2).then((response) => {
      Array.from(response).forEach(({ config, data }) => {
        this[setDataMap.get(config.url)] = data.data || data;
      });
    });
  },
  computed: {
    currentTitle() {
      return this.titles.get(this.currentStep);
    },
    currentInterface() {
      return this.interfaces.get(this.currentStep);
    },
    nationalityOptions() {
      return this.nationalityData.map(({ name }) => name);
    },
    schedulesOptions() {
      return this.schedulesData.map(({ name }) => name);
    },
    vacanciesOptions() {
      return this.vacanciesData?.map(({ name }) => name) || [];
    },
  },
  methods: {
    async userInfoReadyHandler(formData) {
      const { brand } = this.$route.query;
      const candidateToken = JSON.parse(sessionStorage.getItem('candidateToken'));
      const dataToPost = {
        brand,
        firstName: sanitize(formData.firstName),
        lastName: sanitize(formData.lastName),
        patronymicName: sanitize(formData.patronymicName),
        // fullName: sanitize(formData.person),
        citizenship: sanitize(formData.nationality),
        birthday: formData.birthday,
        email: sanitize(formData.email),
        phone: sanitize(formData.phone.replace(/[+\s()-]/g, '')),
        consentData: formData.agreement,
        preferredCommunication: sanitize(this.preferredCommunicationMap.get(formData.communicationMethod)),
        socials: formData.communicationMethod === 'VK' ? [{ type: 'VK', urn: formData.profile }] : [],
      };

      if (candidateToken) {
        await updateCandidateData(candidateToken.candidateId, dataToPost, candidateToken.token);
        sessionStorage.setItem('userInfo', JSON.stringify(formData));
        this.currentStep = 2;
      } else {
        const response = await postCandidate(dataToPost);
        if (response) {
          sessionStorage.setItem('userInfo', JSON.stringify(formData));
          sessionStorage.setItem('candidateToken', JSON.stringify(response.data));

          const token = response.data.token || '';

          http.defaults.headers['X-Authorization'] = token;
          http.defaults.headers['X-Authorization-Candidate'] = token;

          this.currentStep = 2;
        }
      }
    },
    vacancyInfoReadyHandler(formData) {
      sessionStorage.setItem('vacancyInfo', JSON.stringify(formData));
    },
    async questionInfoReadyHandler(formData) {
      const { name: routeName, query } = this.$route;
      sessionStorage.setItem('questionInfo', JSON.stringify(formData));
      const candidateToken = JSON.parse(sessionStorage.getItem('candidateToken'));
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
      const vacancyInfo = JSON.parse(sessionStorage.getItem('vacancyInfo'));
      const currentVacancyId = routeName.toLowerCase() === 'vacancy'
        ? query.position
        : this.vacanciesData.find(({ name }) => name === vacancyInfo.vacancy)?.id;
      const currentOffice = this.officesData.find(({ name }) => name === vacancyInfo.shop);
      const { resume } = formData;
      const source = query?.utm_source;
      const candidateInDetails = {
        brand: 'FRANCHISE',
        city: sanitize(vacancyInfo.cityId),
        consentVideo: formData.agree,
        favoriteSport: formData.kindsOfSport ? sanitize(formData.kindsOfSport) : null,
        healthyLifestyle: formData.healthyLifestyle,
        income: sanitize(vacancyInfo.profit),
        storeAddress: sanitize(currentOffice?.id),
        takeExercise: formData.goSports,
        vacancy: currentVacancyId,
        workSchedule: sanitize(vacancyInfo.schedule),
        source: source || 'landing',
      };
      const candidateData = {
        firstName: sanitize(userInfo.firstName),
        lastName: sanitize(userInfo.lastName),
        patronymicName: sanitize(userInfo.patronymicName),
        // fullName: sanitize(userInfo.person),
        citizenship: sanitize(userInfo.nationality),
        birthday: userInfo.birthday,
        email: sanitize(userInfo.email),
        phone: sanitize(userInfo.phone.replace(/[+\s()-]/g, '')),
        consentData: userInfo.agreement,
        preferredCommunication: sanitize(this.preferredCommunicationMap.get(userInfo.communicationMethod)),
      };

      if (candidateToken) {
        await updateCandidateData(candidateToken.candidateId, candidateData, candidateToken.token);
        await updateCandidateDetail(candidateToken.applicationId, candidateInDetails, candidateToken.token);
        if (resume) {
          const resumeData = new FormData();
          resumeData.append('file', resume[0]);
          try {
            await postResume(candidateToken.applicationId, resumeData, candidateToken.token);
          } catch {
            console.log('error');
          }
        }
        await checkDuplicates(candidateToken.applicationId, candidateToken.token)
          .then(async ({ status }) => {
            if (status === 200) {
              await aplicationConfirm(candidateToken.applicationId, candidateToken.token);
              if (this.$domain === 'ru') {
                try {
                  const result = await getTelegramLink(candidateToken?.applicationId, candidateToken?.token);
                  const { status: telegramRequestStatus, data: { telegramLink } } = result;
                  if (telegramRequestStatus === 200) {
                    this.telegramLink = telegramLink;
                  }
                } catch {
                  toast.danger({
                    title: 'Не удалось получить ссылку ан телеграмм',
                  });
                }
              }
              this.currentStep = 4;
            } else if (status === 409) {
              await aplicationConfirm(candidateToken.applicationId, candidateToken.token);
              if (this.$domain === 'ru') {
                let callCount = 0;
                const intervalId = setInterval(async () => {
                  try {
                    const result = await getTelegramLink(candidateToken?.applicationId);
                    const { status: telegramRequestStatus, data: { telegramLink, candidateId } } = result;
                    if (telegramRequestStatus === 200 && candidateId !== candidateToken.candidateId) {
                      this.telegramLink = telegramLink;
                    }
                  } catch {
                    toast.danger({
                      title: 'Не удалось получить ссылку на телеграмм',
                    });
                  }
                  callCount += 1;
                  if (callCount >= 5) {
                    clearInterval(intervalId);
                  }
                }, 3000);
              }
              this.currentStep = 4;
              sessionStorage.removeItem('candidateToken');
            }
          })
          .catch(() => {
            toast.danger({
              title: 'Сервис временно не доступен, попробуйте позже',
            });
          });
      }
    },
    goNextStep() {
      this.currentStep = this.currentStep === this.maxStep ? this.currentStep : this.currentStep + 1;
    },
    goPrevStep() {
      this.currentStep = this.currentStep === this.minStep ? this.currentStep : this.currentStep - 1;
    },
  },
};
</script>
